/**
 * Impact GRAPH (#182 rebuild) — pure data/layout logic, no React.
 *
 * This module turns the tree fetched by `GET /items/{id}/impact-tree`
 * (`tree.root` / `tree.upstream` / `tree.downstream`, each upstream/downstream
 * node carrying `children`/`link_type`/`verifies`, the root carrying its own
 * `verifies`) into positioned React Flow node/edge arrays, via `@dagrejs/dagre`
 * for the actual layered-graph placement. Kept framework-agnostic (no JSX, no
 * hooks) so the layout algorithm is unit-testable in isolation from rendering.
 *
 * Direction semantics are NOT re-derived here — every upstream/downstream/
 * verification classification comes straight from the backend
 * (`core.requirements.impact_tree`, itself reusing `core.coverage.is_verification_link`).
 * This module only decides WHERE to draw what the backend already told us.
 *
 * --- Graph model -----------------------------------------------------------
 * `buildGraphModel(tree, visibleTypes)` walks the tree once and produces a
 * mode-agnostic model: `{ nodes, edges, verifyMap }`.
 *   - `nodes`: root + every visible ancestor/descendant (unchecking a
 *     relationship-type checkbox prunes that node AND its whole subtree, same
 *     as the previous implementation — a link_type not in `visibleTypes` is
 *     never descended into).
 *   - `edges`: lineage edges only, already oriented so `source` renders ABOVE
 *     `target` in a top-down (TB) dagre layout: an ancestor's edge points
 *     ancestor -> nearer-to-root; a descendant's edge points nearer-to-root ->
 *     descendant. This single convention is what lets `layoutFamilyTree` and
 *     `layoutVModel`'s first pass share one `runDagre` helper.
 *   - `verifyMap`: `{ ownerId, entries }[]` — each lineage node's OWN
 *     verification targets (root's own `verifies`, or a lineage node's
 *     `verifies`), kept separate from `edges` because the two layout modes
 *     place them completely differently (folded into the same TB flow in
 *     family-tree mode; a fixed right-hand column with horizontal rungs in
 *     V-model mode).
 *
 * --- Layout modes ------------------------------------------------------
 * `layoutFamilyTree`: ONE dagre graph, `rankdir: 'TB'` — ancestors above root,
 * descendants below, each node's verify-satellites folded in as extra leaf
 * edges hanging off their owner (dagre's own spacing keeps them from
 * overlapping their owner or any sibling — the literal "overlapping nodes"
 * bug found in the previous hand-rolled version).
 *
 * `layoutVModel`: dagre lays out the lineage chain alone (`rankdir: 'TB'`);
 * verify-satellites are then placed manually in a fixed right-hand column, at
 * the SAME y as their owner (so the connecting edge reads as a roughly
 * horizontal "rung"), stacked vertically only when two owners' satellite
 * groups would otherwise collide.
 *
 * --- Titles/ids sizing (#182 polish round 2, item 6) ------------------
 * `showTitles` and `showIds` are now two INDEPENDENT toggles (were one
 * `showTitles` switch). When titles are shown they render in FULL — no
 * ellipsis truncation of a reasonably-sized title — so a node's width can no
 * longer be one fixed constant; `estimateNodeWidth()` sizes it from the
 * title's character count and that SAME estimate is what's fed to dagre
 * (`nodeSize()`), so the layout reserves the actual room a wider node will
 * render at and siblings don't overlap once titles grow the boxes. The CSS
 * side (`ImpactGraph.css` `.ig-node`) mirrors this by sizing to 100% of the
 * React-Flow-positioned wrapper rather than clamping at a fixed px cap. A
 * generous but finite cap (`MAX_TITLE_NODE_WIDTH`) still exists purely as a
 * safety valve for pathologically long titles / estimate error — see the
 * ellipsis fallback in `ImpactGraph.css`.
 *
 * --- Edge handle fan-out (#182 polish, item 9) -------------------------
 * Every node exposes several source/target handles per side (see
 * `H_SLOT_COUNT`/`V_SLOT_COUNT`, consumed by `ImpactGraphNode.js` to render
 * that many `<Handle>`s spaced along the side) instead of one handle per
 * side. Edges of the SAME relationship type share one entry/exit point (they
 * read as "one kind of link") while DIFFERENT types get distinct points so
 * their colours don't overlap right at the node. Per PO preference (#182):
 * same-type edges SHARING a common point is the desired look — a per-edge
 * variant that split every individual edge onto its own slot was tried and
 * explicitly rejected.
 *
 * #182 follow-up bug fix, pass 1 (2026-07-08): the type -> slot mapping used
 * to be a character-code hash of the type string, which had NO uniqueness
 * guarantee — two different relationship types (e.g. `verifies` and
 * `satisfies`, or `verifies` and `derives_from`) could hash to the same slot
 * purely by coincidence, exactly reproducing the "different types share a
 * connection point" bug this whole scheme exists to avoid. `handleId()` (used
 * for the V-model's right/left verify "rung" column, which has fixed column
 * geometry) takes the tree's own ordered, deduped type list
 * (`collectLinkTypes`) and looks up each type's fixed INDEX in it, so
 * distinct types get distinct slots whenever the slot count allows.
 *
 * #182 follow-up bug fix, pass 2 (2026-07-08): that index is ALPHABETICAL,
 * which fixed the collision but not a second, distinct bug the PO reported in
 * the same comment — "the verifies nodes are to the left, and the satisfies
 * node is to the right, but the connections are swapped so the lines
 * overlap." An alphabetical slot has no relationship to where a type's target
 * actually sits on screen. For bottom/top handles (the lineage direction in
 * both layout modes, and the family-tree verify satellites), `assignSpatialHandles()`
 * replaces the alphabetical index: it runs AFTER dagre layout, groups each
 * node's touching edges by type (grouping unchanged), and orders the groups by
 * the real on-screen X of their other endpoint — so the leftmost group gets
 * the leftmost slot regardless of type name.
 */
import dagre from '@dagrejs/dagre';
import { lineageVerb, humanizeLinkType } from '../../utils/linkGrammar';

// Number of fan-out handle slots per side — see module docstring "Edge handle
// fan-out". Bottom/top (the lineage direction in both layout modes) get more
// slots since that's where multiple relationship types most often converge;
// left/right (only ever used for the V-model's verify "rung") get fewer.
export const H_SLOT_COUNT = 5;
export const V_SLOT_COUNT = 3;

// Edge handle fan-out — separate edges by RELATIONSHIP TYPE at the node.
// Per PO preference (#182): edges of the SAME relationship type SHOULD share a
// common entry/exit point on a node (they read as "one kind of link"), while
// DIFFERENT types leave/enter at distinct points so their colours don't overlap
// right at the node. Slot = the type's fixed index in the tree's own ordered
// type list (see module docstring "Edge handle fan-out" for why this replaced
// a plain string hash) — same type always resolves to the same slot, and
// distinct types get distinct slots as long as there are enough of them. Falls
// back to the old character-hash when a type isn't in the index (no
// `linkTypes` supplied at all, or a type that slipped in after the index was
// built) — still deterministic, just without the collision-free guarantee.
//
// (A per-edge variant that gave every individual edge its own slot was tried
// and explicitly rejected by the PO — same-type siblings sharing a point is the
// desired look, not a bug.)
function hashSlot(linkType, count) {
  let h = 0;
  for (let i = 0; i < linkType.length; i += 1) h = (h * 31 + linkType.charCodeAt(i)) >>> 0;
  return h % count;
}

function slotFor(linkType, count, typeIndex) {
  if (!linkType) return Math.floor(count / 2);
  const idx = typeIndex ? typeIndex.get(linkType) : undefined;
  return idx === undefined ? hashSlot(linkType, count) : idx % count;
}

function handleId(side, linkType, typeIndex) {
  const count = side === 'left' || side === 'right' ? V_SLOT_COUNT : H_SLOT_COUNT;
  return `${side}-${slotFor(linkType, count, typeIndex)}`;
}

// #182 follow-up (2026-07-08, second pass): the collision fix above (index-in-
// `collectLinkTypes`) guarantees distinct types get distinct bottom/top slots,
// but that index is ALPHABETICAL — it has no relationship to where each
// type's actual target/source sits on screen. PO report: "the verifies nodes
// are to the left, and the satisfies node is to the right, but the
// connections are swapped so the lines overlap" — i.e. a node whose
// left-positioned child happens to have an alphabetically-later type than its
// right-positioned child still got its exit handle on the crossing side.
//
// Fix: for each node's bottom (outgoing) and top (incoming) side, group the
// touching edges by type (same-type-shares-a-point is still the desired
// look — unchanged), order the type groups by the average on-screen X of
// their *other* endpoint, and hand out slots 0..N-1 in that spatial order —
// so the leftmost group gets the leftmost slot regardless of type name. Runs
// AFTER dagre (needs real x positions), so this replaces the alphabetical
// handleId() call for bottom/top edges; V-model's left/right verify "rung"
// column still uses the type-index scheme (different geometry — the rung's
// column position is already fixed by design, not derived from a fan-out).
function assignSpatialHandles(edges, positionedById, sourceSide, targetSide) {
  const bySourceNode = new Map();
  const byTargetNode = new Map();
  edges.forEach((e) => {
    if (!bySourceNode.has(e.source)) bySourceNode.set(e.source, []);
    bySourceNode.get(e.source).push(e);
    if (!byTargetNode.has(e.target)) byTargetNode.set(e.target, []);
    byTargetNode.get(e.target).push(e);
  });

  const slotMapFor = (edgesForNode, otherKey, count) => {
    const xByType = new Map();
    edgesForNode.forEach((e) => {
      const other = positionedById.get(e[otherKey]);
      const x = other ? other.x + other.width / 2 : 0;
      if (!xByType.has(e.linkType)) xByType.set(e.linkType, []);
      xByType.get(e.linkType).push(x);
    });
    const ordered = [...xByType.entries()]
      .map(([linkType, xs]) => ({ linkType, avgX: xs.reduce((s, v) => s + v, 0) / xs.length }))
      .sort((a, b) => a.avgX - b.avgX);
    const n = ordered.length;
    const slot = new Map();
    ordered.forEach(({ linkType }, i) => {
      slot.set(linkType, n <= 1 ? Math.floor(count / 2) : Math.round((i / (n - 1)) * (count - 1)));
    });
    return slot;
  };

  const sourceSlotByNode = new Map([...bySourceNode.entries()].map(([id, list]) => [id, slotMapFor(list, 'target', H_SLOT_COUNT)]));
  const targetSlotByNode = new Map([...byTargetNode.entries()].map(([id, list]) => [id, slotMapFor(list, 'source', H_SLOT_COUNT)]));

  return edges.map((e) => ({
    ...e,
    sourceHandle: `${sourceSide}-${sourceSlotByNode.get(e.source).get(e.linkType)}`,
    targetHandle: `${targetSide}-${targetSlotByNode.get(e.target).get(e.linkType)}`,
  }));
}

// Full-title node sizing (item 6). #182 follow-up (2026-07-08, PO ask: "nodes
// need to be 50% narrower and 2 lines of title rather than 1"): titles now
// WRAP onto up to 2 lines (`.ig-node-title` in ImpactGraph.css) instead of a
// single `nowrap` line, so a node no longer needs enough width to hold the
// whole title on one line — roughly HALF the characters need to fit per line,
// so the width estimate is halved too (MIN/MAX/the character budget all ~50%
// of their single-line values) and `TITLE_NODE_HEIGHT` grows to fit a second
// line. Still capped (`MAX_TITLE_NODE_WIDTH`) so a pathologically long title
// can't blow out the layout — the CSS `-webkit-line-clamp: 2` ellipsis is the
// fallback beyond 2 lines' worth of text.
const MIN_TITLE_NODE_WIDTH = 72;
export const MAX_TITLE_NODE_WIDTH = 210;
const CHARS_PER_PX = 6.4;
const ID_CHIP_ALLOWANCE = 64; // extra width reserved when the id chip is ALSO shown alongside the title
const TITLE_NODE_HEIGHT = 54; // was 40 (1 line) — tall enough for 2 wrapped lines + padding

function estimateNodeWidth(title, showIds) {
  const base = Math.max(MIN_TITLE_NODE_WIDTH, Math.round(((title || '').length / 2) * CHARS_PER_PX) + 32);
  const width = showIds ? base + ID_CHIP_ALLOWANCE : base;
  return Math.min(MAX_TITLE_NODE_WIDTH, width);
}

// Stack rows (item 2) are single-line (`nowrap`, not the main node's 2-line
// wrap), so they need closer to the FULL single-line width estimate, not the
// halved-for-2-lines one `estimateNodeWidth` uses — otherwise every row
// mid-word-clips well before its ellipsis has any real title left to show.
function estimateStackRowWidth(title, showIds) {
  const base = Math.max(MIN_TITLE_NODE_WIDTH, Math.round((title || '').length * CHARS_PER_PX * 0.72) + 32);
  const width = showIds ? base + ID_CHIP_ALLOWANCE : base;
  return Math.min(MAX_TITLE_NODE_WIDTH, width);
}

// Stable colour per relationship type (first-seen/alphabetical order so it's
// deterministic across renders of the same tree).
export const EDGE_COLORS = ['#1971c2', '#e8590c', '#2f9e44', '#9c36b5', '#e03131', '#0c8599', '#f08c00', '#495057'];
// Fallback for the root / any node without its own relationship colour (e.g. a
// canvas-rendered surface like the MiniMap, where a CSS var() fill is less
// reliably supported than in a DOM element's inline style).
export const ROOT_COLOR = '#3b6fd9';

export const MAX_INLINE_VERIFIES = 3; // family-tree + v-model: cap inline satellites, "+N more" beyond this
const VMODEL_GAP = 120;               // v-model: extra x gap between the lineage arm and the verify column
const SAT_ROW_H = 44;                 // v-model: vertical spacing between stacked satellites when groups collide

// #182 follow-up (2026-07-08, item 2 — PO "trial" suggestion): "in the case
// where there are multiple children with the same relationship type, and
// there are no further child nodes, that set of nodes could all be stacked
// and use just a single connection line into the top of them... where a node
// has ancestor links of a single type with multiple ancestors of the same
// link type, those could also be stacked." See `collapseLeafStacks()` below.
export const STACK_MIN = 2;      // 2+ same-type leaf siblings collapse into one stack node
export const STACK_MAX_SHOWN = 4; // rows shown inside the stack box before "+N more"
const STACK_ROW_H = 20;

export function collectLinkTypes(tree) {
  const types = new Set();
  const visit = (list) => {
    (list || []).forEach((n) => {
      if (n.link_type) types.add(n.link_type);
      (n.verifies || []).forEach((v) => v.link_type && types.add(v.link_type));
      if (Array.isArray(n.children)) visit(n.children);
    });
  };
  if (tree) {
    visit(tree.upstream);
    visit(tree.downstream);
    const rootVerifies = (tree.root && tree.root.verifies) || tree.verification || [];
    rootVerifies.forEach((v) => v.link_type && types.add(v.link_type));
  }
  return Array.from(types).sort();
}

function nodeSize(node, { showTitles, showIds }) {
  const { kind, title } = node;
  if (kind === 'verify-more') return { width: 84, height: 26 };
  if (kind === 'stack') {
    const shown = Math.min(node.members.length, STACK_MAX_SHOWN);
    const widest = node.members.reduce((w, m) => Math.max(w, estimateStackRowWidth(m.title, showIds)), MIN_TITLE_NODE_WIDTH);
    return { width: widest, height: 28 + shown * STACK_ROW_H + (node.members.length > shown ? STACK_ROW_H : 0) };
  }
  if (showTitles) return { width: estimateNodeWidth(title, showIds), height: TITLE_NODE_HEIGHT };
  return { width: showIds ? 100 : 64, height: 40 };
}

// Recursively walk one lineage branch (upstream or downstream), pruning any
// node whose link_type isn't in `visibleTypes` (and, transitively, everything
// below it — matching the pre-existing prune-node-and-subtree behaviour).
function walkLineage(list, contextParentId, visibleTypes, keyPrefix, kind, nodes, edges, verifyMap) {
  (list || []).forEach((n, idx) => {
    if (!visibleTypes.has(n.link_type)) return;
    const id = `${keyPrefix}.${idx}.${n.id}`;
    nodes.push({
      id, itemId: n.id, display_id: n.display_id, title: n.title,
      type_id: n.type_id, status: n.status, kind, linkType: n.link_type,
    });
    if (kind === 'ancestor') {
      // Ancestor renders ABOVE the node it's ancestor-of: source -> target = ancestor -> nearer-to-root.
      edges.push({ id: `e:${id}>${contextParentId}`, source: id, target: contextParentId, kind: 'lineage', linkType: n.link_type });
    } else {
      // Descendant renders BELOW the node it descends from: source -> target = nearer-to-root -> descendant.
      edges.push({ id: `e:${contextParentId}>${id}`, source: contextParentId, target: id, kind: 'lineage', linkType: n.link_type });
    }
    const verifies = (n.verifies || []).filter((v) => visibleTypes.has(v.link_type));
    if (verifies.length) verifyMap.push({ ownerId: id, entries: verifies });
    if (Array.isArray(n.children)) walkLineage(n.children, id, visibleTypes, id, kind, nodes, edges, verifyMap);
  });
}

// #182 follow-up (2026-07-08, item 2 — PO "trial" suggestion): collapse
// eligible LEAF lineage siblings into one "stack" node with a single
// connection line. A node is eligible when:
//   - it's a lineage leaf (a descendant with no further descendants of its
//     own, or an ancestor with no further ancestors above it — see the
//     source/target direction comment in `walkLineage`);
//   - it owns no verify links of its own (it has significant content beyond
//     "just a leaf slot," so it stays a normal, individually-connected node);
//   - it shares its (attachment point, direction, relationship type) with at
//     least `STACK_MIN` siblings.
// Individual member tooltips/links are preserved (see `ImpactGraphNode.js`'s
// stack rendering) — this only removes the per-member CANVAS node + edge, not
// the per-member identity.
function collapseLeafStacks(nodes, edges, verifyMap) {
  const ownsVerify = new Set(verifyMap.map((v) => v.ownerId));
  const hasOutgoingSource = new Set(edges.map((e) => e.source));
  const hasIncomingTarget = new Set(edges.map((e) => e.target));

  const groups = new Map();
  nodes.forEach((n) => {
    if (n.kind !== 'ancestor' && n.kind !== 'descendant') return;
    if (ownsVerify.has(n.id)) return;
    const isLeaf = n.kind === 'descendant' ? !hasOutgoingSource.has(n.id) : !hasIncomingTarget.has(n.id);
    if (!isLeaf) return;
    const edge = n.kind === 'descendant'
      ? edges.find((e) => e.target === n.id)
      : edges.find((e) => e.source === n.id);
    if (!edge) return;
    const attachId = n.kind === 'descendant' ? edge.source : edge.target;
    const key = `${attachId}|${n.kind}|${n.linkType}`;
    if (!groups.has(key)) groups.set(key, { attachId, kind: n.kind, linkType: n.linkType, members: [] });
    groups.get(key).members.push(n);
  });

  const collapsedIds = new Set();
  const stackNodes = [];
  const stackEdges = [];
  groups.forEach((g) => {
    if (g.members.length < STACK_MIN) return;
    g.members.forEach((m) => collapsedIds.add(m.id));
    const stackId = `stack:${g.attachId}:${g.kind}:${g.linkType}`;
    stackNodes.push({
      id: stackId, kind: 'stack', linkType: g.linkType,
      members: g.members.map((m) => ({ id: m.itemId, display_id: m.display_id, title: m.title, type_id: m.type_id, status: m.status })),
    });
    stackEdges.push(g.kind === 'descendant'
      ? { id: `e:${g.attachId}>${stackId}`, source: g.attachId, target: stackId, kind: 'lineage', linkType: g.linkType }
      : { id: `e:${stackId}>${g.attachId}`, source: stackId, target: g.attachId, kind: 'lineage', linkType: g.linkType });
  });

  if (!collapsedIds.size) return { nodes, edges };
  return {
    nodes: [...nodes.filter((n) => !collapsedIds.has(n.id)), ...stackNodes],
    edges: [...edges.filter((e) => !collapsedIds.has(e.source) && !collapsedIds.has(e.target)), ...stackEdges],
  };
}

// `stackLeaves` (default true) — collapses same-type leaf siblings into stacks.
export function buildGraphModel(tree, visibleTypes, { stackLeaves = true } = {}) {
  const nodes = [];
  const edges = [];
  const verifyMap = [];
  if (!tree || !tree.root) return { nodes, edges, verifyMap };

  nodes.push({
    id: 'root', itemId: tree.root.id, display_id: tree.root.display_id, title: tree.root.title,
    type_id: tree.root.type_id, status: tree.root.status, kind: 'root', linkType: null,
  });
  const rootVerifiesRaw = tree.root.verifies || tree.verification || [];
  const rootVerifies = rootVerifiesRaw.filter((v) => visibleTypes.has(v.link_type));
  if (rootVerifies.length) verifyMap.push({ ownerId: 'root', entries: rootVerifies });

  walkLineage(tree.upstream, 'root', visibleTypes, 'up', 'ancestor', nodes, edges, verifyMap);
  walkLineage(tree.downstream, 'root', visibleTypes, 'down', 'descendant', nodes, edges, verifyMap);

  if (!stackLeaves) return { nodes, edges, verifyMap, stackLeaves: false };
  const collapsed = collapseLeafStacks(nodes, edges, verifyMap);
  return { nodes: collapsed.nodes, edges: collapsed.edges, verifyMap, stackLeaves };
}

// Expand each owner's `entries` into capped inline satellite nodes/edges + an
// optional "+N more" overflow node (a handful of test cases per requirement is
// the common case; this keeps the layout from blowing out for a heavily-tested node).
function expandVerifies(verifyMap, stackLeaves = false) {
  const vNodes = [];
  const vEdges = [];
  verifyMap.forEach(({ ownerId, entries }) => {
    if (stackLeaves) {
      const byType = new Map();
      entries.forEach((e) => {
        const key = e.link_type || '';
        if (!byType.has(key)) byType.set(key, []);
        byType.get(key).push(e);
      });
      const singles = [];
      byType.forEach((group, linkType) => {
        if (group.length >= STACK_MIN) {
          const stackId = `vstack:${ownerId}:${linkType}`;
          vNodes.push({
            id: stackId, kind: 'stack', linkType: linkType || null,
            members: group.map((v) => ({ id: v.id, display_id: v.display_id, title: v.title, type_id: v.type_id, status: v.status })),
            ownerId,
          });
          vEdges.push({ id: `e:${ownerId}>${stackId}`, source: ownerId, target: stackId, kind: 'verify', linkType: linkType || null });
        } else {
          singles.push(...group);
        }
      });
      const shown = singles.slice(0, MAX_INLINE_VERIFIES);
      shown.forEach((v, i) => {
        const id = `${ownerId}#v${i}-${v.id}`;
        vNodes.push({ id, itemId: v.id, display_id: v.display_id, title: v.title, kind: 'verify', linkType: v.link_type, ownerId });
        vEdges.push({ id: `e:${ownerId}>${id}`, source: ownerId, target: id, kind: 'verify', linkType: v.link_type });
      });
      const overflow = singles.length - shown.length;
      if (overflow > 0) {
        const id = `${ownerId}#vmore`;
        vNodes.push({ id, itemId: null, display_id: null, title: `+${overflow} more`, kind: 'verify-more', linkType: null, ownerId });
        vEdges.push({ id: `e:${ownerId}>${id}`, source: ownerId, target: id, kind: 'verify-more', linkType: null });
      }
    } else {
      const shown = entries.slice(0, MAX_INLINE_VERIFIES);
      shown.forEach((v, i) => {
        const id = `${ownerId}#v${i}-${v.id}`;
        vNodes.push({ id, itemId: v.id, display_id: v.display_id, title: v.title, kind: 'verify', linkType: v.link_type, ownerId });
        vEdges.push({ id: `e:${ownerId}>${id}`, source: ownerId, target: id, kind: 'verify', linkType: v.link_type });
      });
      const overflow = entries.length - shown.length;
      if (overflow > 0) {
        const id = `${ownerId}#vmore`;
        vNodes.push({ id, itemId: null, display_id: null, title: `+${overflow} more`, kind: 'verify-more', linkType: null, ownerId });
        vEdges.push({ id: `e:${ownerId}>${id}`, source: ownerId, target: id, kind: 'verify-more', linkType: null });
      }
    }
  });
  return { vNodes, vEdges };
}

function runDagre(nodes, edges, { rankdir, nodesep, ranksep, titleOpts }) {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir, nodesep, ranksep, marginx: 24, marginy: 24 });
  g.setDefaultEdgeLabel(() => ({}));
  nodes.forEach((n) => {
    const { width, height } = nodeSize(n, titleOpts);
    g.setNode(n.id, { width, height });
  });
  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);
  return nodes.map((n) => {
    const pos = g.node(n.id) || { x: 0, y: 0 };
    const { width, height } = nodeSize(n, titleOpts);
    return { ...n, x: pos.x - width / 2, y: pos.y - height / 2, width, height };
  });
}

// Family-tree mode: one dagre graph, verify-satellites folded in as extra
// leaf edges off their owner — see module docstring.
export function layoutFamilyTree(model, { showTitles, showIds }) {
  const titleOpts = { showTitles, showIds };
  const { vNodes, vEdges } = expandVerifies(model.verifyMap, model.stackLeaves);
  const allNodes = [...model.nodes, ...vNodes];
  const allEdges = [...model.edges, ...vEdges];
  const positionedByKind = new Map(
    runDagre(allNodes, allEdges.map(({ source, target }) => ({ source, target })), { rankdir: 'TB', nodesep: 30, ranksep: 64, titleOpts })
      .map((n) => [n.id, n])
  );
  const nodes = allNodes.map((n) => positionedByKind.get(n.id));
  // Bottom/top handles assigned by real on-screen x-order, not type name —
  // see `assignSpatialHandles` module comment.
  const edges = assignSpatialHandles(allEdges, positionedByKind, 'bottom', 'top');
  return { nodes, edges };
}

// V-model mode: dagre lays out the lineage chain alone; verify-satellites go
// in a fixed right-hand column at their owner's own y (a "rung") — see module docstring.
export function layoutVModel(model, { showTitles, showIds, linkTypes }) {
  const titleOpts = { showTitles, showIds };
  const typeIndex = new Map((linkTypes || []).map((t, i) => [t, i]));
  const lineagePositioned = runDagre(model.nodes, model.edges, { rankdir: 'TB', nodesep: 30, ranksep: 90, titleOpts });
  const byId = new Map(lineagePositioned.map((n) => [n.id, n]));
  const maxRightX = lineagePositioned.length ? Math.max(...lineagePositioned.map((n) => n.x + n.width)) : 0;
  const rightColX = maxRightX + VMODEL_GAP;

  const { vNodes, vEdges } = expandVerifies(model.verifyMap, model.stackLeaves);
  const grouped = new Map();
  vNodes.forEach((vn) => {
    const arr = grouped.get(vn.ownerId) || [];
    arr.push(vn);
    grouped.set(vn.ownerId, arr);
  });

  // Walk owners top-to-bottom by their own y so overlapping satellite groups
  // (two nearby lineage nodes each with their own verify targets) get pushed
  // apart rather than rendered on top of one another.
  const ownerGroups = [...grouped.entries()]
    .map(([ownerId, list]) => ({ ownerId, list, owner: byId.get(ownerId) }))
    .filter((g) => g.owner)
    .sort((a, b) => a.owner.y - b.owner.y);

  const satellites = [];
  let cursorBottom = -Infinity;
  ownerGroups.forEach(({ list, owner }) => {
    const n = list.length;
    const groupHeight = (n - 1) * SAT_ROW_H;
    let startY = owner.y + owner.height / 2 - groupHeight / 2;
    if (startY < cursorBottom + 8) startY = cursorBottom + 8;
    list.forEach((vn, i) => {
      const { width, height } = nodeSize(vn, titleOpts);
      satellites.push({ ...vn, x: rightColX, y: startY + i * SAT_ROW_H, width, height });
    });
    const groupBottom = startY + groupHeight + (nodeSize(list[n - 1], titleOpts).height);
    cursorBottom = Math.max(cursorBottom, groupBottom);
  });

  const nodes = [...lineagePositioned, ...satellites];
  // Lineage bottom/top handles: same spatial-order fix as family-tree mode.
  // Verify "rung" left/right handles: unchanged type-index scheme — the
  // rung's column position is already fixed by the V-model layout itself,
  // not a fan-out that can cross the way bottom/top siblings can.
  const edges = [
    ...assignSpatialHandles(model.edges, byId, 'bottom', 'top'),
    ...vEdges.map((e) => (
      { ...e, sourceHandle: handleId('right', e.linkType, typeIndex), targetHandle: handleId('left', e.linkType, typeIndex) }
    )),
  ];
  return { nodes, edges };
}

// Convert a positioned {nodes, edges} model into React Flow-ready arrays.
// `palette` maps link_type -> colour (built by the component from `collectLinkTypes`).
export function toReactFlowGraph(positioned, { palette, showHumanIds, showTitles, showIds }) {
  const nodes = positioned.nodes.map((n) => ({
    id: n.id,
    type: 'impactNode',
    position: { x: n.x, y: n.y },
    // React Flow needs an explicit size hint for nodes not yet measured (e.g. in
    // jsdom, where ResizeObserver never fires with a real size) so layout-derived
    // positions aren't immediately re-measured/discarded. Also — unlike a
    // measured size — an EXPLICIT width/height here keeps winning even after
    // measurement (see @xyflow/react's getNodeInlineStyleDimensions), which is
    // what lets the CSS side just be `width: 100%` and trust this estimate.
    width: n.width,
    height: n.height,
    // #182 polish round 2 (item 4): this is a derived/computed dagre layout,
    // not something a user should be able to rearrange — dragging a node here
    // would just silently be discarded on the next re-layout anyway.
    draggable: false,
    selectable: n.kind !== 'verify-more',
    data: {
      itemId: n.itemId, displayId: n.display_id, title: n.title, typeId: n.type_id, status: n.status,
      kind: n.kind, linkType: n.linkType, showTitles, showIds, showHumanIds, members: n.members,
      color: n.linkType ? palette.get(n.linkType) : (n.kind === 'root' ? undefined : ROOT_COLOR),
    },
  }));

  const edges = positioned.edges.map((e) => {
    const color = e.linkType ? (palette.get(e.linkType) || '#adb5bd') : '#adb5bd';
    const dashed = e.kind === 'verify' || e.kind === 'verify-more';
    return {
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle,
      targetHandle: e.targetHandle,
      type: 'smoothstep',
      pathOptions: { borderRadius: 12 },
      // #182 polish round 2 (item 8): a small, unobtrusive text label naming the
      // relationship type — colour alone isn't legible/accessible enough,
      // especially once several types are on screen at once.
      // #374: a LINEAGE edge is drawn top→bottom, so its label must read that way
      // — the downstream (lower) node's role from the upstream node's side (raw
      // stored `parent` → "Child", `derives_from` → "Derived by"). Verify edges
      // aren't a lineage pair, so they're just humanised (`verifies` → "Verifies").
      // `e.linkType` itself is left untouched — handle-slot grouping keys on it.
      label: e.linkType
        ? (e.kind === 'lineage' ? lineageVerb(e.linkType) : humanizeLinkType(e.linkType))
        : undefined,
      labelStyle: { fill: 'var(--text-3)', fontSize: 9.5, fontWeight: 600 },
      labelBgStyle: { fill: 'var(--base)', fillOpacity: 0.92 },
      labelBgPadding: [3, 2],
      labelBgBorderRadius: 3,
      style: {
        stroke: color,
        strokeWidth: dashed ? 1.5 : 2,
        strokeDasharray: dashed ? '5 4' : undefined,
        opacity: e.kind === 'verify-more' ? 0.5 : 1,
      },
      markerEnd: { type: 'arrowclosed', color, width: 16, height: 16 },
      zIndex: 0,
    };
  });

  return { nodes, edges };
}
